import {
  type DynamicClient,
  InvalidParamError,
} from '@dynamic-labs-sdk/client';
import type {
  ExecuteCheckoutTransactionParams,
  ExecuteCheckoutTransactionResult,
} from '@dynamic-labs-sdk/client/core';
import {
  getBuffer,
  getWalletProviderFromWalletAccount,
} from '@dynamic-labs-sdk/client/core';
import { Transaction, VersionedTransaction } from '@solana/web3.js';

import type { SolanaSigningPayload } from '../../checkoutTransaction.types';
import { isSolanaWalletAccount } from '../../isSolanaWalletAccount';
import { isSolanaWalletProvider } from '../../isSolanaWalletProvider';

/**
 * Executes a Solana checkout transaction by deserializing the base64-encoded
 * transaction and sending it via the wallet provider's signAndSendTransaction.
 *
 * 1. Validates that the wallet account is a Solana wallet account
 * 2. Extracts and validates the serialized transaction from the signing payload
 * 3. Deserializes the transaction (tries VersionedTransaction first, falls back to Transaction)
 * 4. Signs and sends via the Solana wallet provider
 *
 * @param params.signingPayload - The signing payload containing `serializedTransaction`.
 * @param params.walletAccount - The wallet account used to sign and send the transaction.
 * @param params.onStepChange - Optional callback invoked with `'transaction'` when the send step begins.
 * @param client - The Dynamic client instance.
 * @returns The transaction hash (signature).
 */
export const solanaExecuteCheckoutTransaction = async (
  {
    onStepChange,
    signingPayload,
    walletAccount,
  }: ExecuteCheckoutTransactionParams,
  client: DynamicClient
): Promise<ExecuteCheckoutTransactionResult> => {
  const walletProvider = getWalletProviderFromWalletAccount(
    { walletAccount },
    client
  );

  if (
    !isSolanaWalletAccount(walletAccount) ||
    !isSolanaWalletProvider(walletProvider)
  ) {
    throw new InvalidParamError(
      'Wallet account is not a Solana wallet account'
    );
  }

  const solanaPayload = signingPayload as SolanaSigningPayload;

  if (!solanaPayload.serializedTransaction?.serializedTransaction) {
    throw new InvalidParamError(
      'Signing payload is missing serialized transaction data'
    );
  }

  const { serializedTransaction: serializedTransactionStr } =
    solanaPayload.serializedTransaction;

  const bytes = new Uint8Array(
    getBuffer().from(serializedTransactionStr, 'base64')
  );

  let transaction: Transaction | VersionedTransaction;

  // Try VersionedTransaction first, fall back to legacy Transaction
  try {
    transaction = VersionedTransaction.deserialize(bytes);
  } catch {
    transaction = Transaction.from(bytes);
  }

  onStepChange?.('transaction');

  const { signature } = await walletProvider.signAndSendTransaction({
    transaction,
    walletAccount,
  });

  return { transactionHash: signature };
};
